import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  TrendingUp,
  Loader2,
  Target,
  PieChart,
  BarChart3,
  ScrollText,
  Landmark,
  Wallet,
  Plus,
  Info,
  ChevronRight,
  Sparkles,
  Clock
} from 'lucide-react';
import Layout from '../components/Layout';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colors for asset types
const ASSET_COLORS = {
  stock: { color: '#10b981', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-600' },
  utt: { color: '#14b8a6', bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-600' },
  etf: { color: '#f97316', bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-600' },
  bond: { color: '#8b5cf6', bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-600' },
  tbill: { color: '#6366f1', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-600' }
};

const ASSET_ICONS = {
  stock: TrendingUp,
  utt: PieChart,
  etf: BarChart3,
  bond: ScrollText,
  tbill: Landmark
};

const ASSET_LABELS = {
  stock: { en: 'Stocks', sw: 'Hisa' },
  utt: { en: 'UTT Funds', sw: 'Mifuko UTT' },
  etf: { en: 'ETFs', sw: 'ETFs' },
  bond: { en: 'Bonds', sw: 'Bondi' },
  tbill: { en: 'T-Bills', sw: 'Dhamana' }
};

export default function PortfolioProjection() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [portfolio, setPortfolio] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [years, setYears] = useState(5);
  const [monthlyContribution, setMonthlyContribution] = useState(0);
  const [scenario, setScenario] = useState('moderate');
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch portfolio data
  const fetchPortfolio = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setPortfolio(response.data);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run simulation
  const runSimulation = useCallback(async () => {
    if (!portfolio?.items?.length) return;
    
    setSimulating(true);
    try {
      const response = await axios.post(`${API}/portfolio/simulate`, {
        years,
        monthly_contribution: monthlyContribution,
        scenario
      });
      setSimulation(response.data);
    } catch (error) {
      console.error('Failed to run simulation:', error);
    } finally {
      setSimulating(false);
    }
  }, [portfolio, years, monthlyContribution, scenario]);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  useEffect(() => {
    if (portfolio?.items?.length) {
      runSimulation();
    }
  }, [portfolio, years, monthlyContribution, scenario]);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'TZS 0';
    if (amount >= 1000000000) {
      return `TZS ${(amount / 1000000000).toFixed(2)}B`;
    } else if (amount >= 1000000) {
      return `TZS ${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `TZS ${(amount / 1000).toFixed(1)}K`;
    }
    return `TZS ${amount.toLocaleString()}`;
  };

  const formatNumber = (amount) => {
    if (!amount && amount !== 0) return '0';
    if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(2)}B`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toLocaleString();
  };

  // Get chart data
  const getChartData = () => {
    if (!simulation?.yearly_breakdown) return [];
    
    const currentValue = simulation.current_value || 0;
    const data = [{ 
      year: t('Now', 'Sasa'), 
      conservative: currentValue, 
      moderate: currentValue, 
      aggressive: currentValue,
      contributions: currentValue
    }];
    
    simulation.yearly_breakdown.forEach((year, index) => {
      // Calculate conservative and aggressive based on moderate
      const conservativeRatio = simulation.scenarios?.conservative?.projected_value / simulation.scenarios?.moderate?.projected_value || 0.85;
      const aggressiveRatio = simulation.scenarios?.aggressive?.projected_value / simulation.scenarios?.moderate?.projected_value || 1.15;
      
      data.push({
        year: `${t('Year', 'Mwaka')} ${year.year}`,
        conservative: Math.round(year.end_value * conservativeRatio),
        moderate: year.end_value,
        aggressive: Math.round(year.end_value * aggressiveRatio),
        contributions: year.cumulative_contributions || (currentValue + monthlyContribution * 12 * year.year)
      });
    });
    
    return data;
  };

  // Get allocation data for pie chart
  const getAllocationData = () => {
    if (!portfolio?.items) return [];
    
    const allocation = {};
    portfolio.items.forEach(item => {
      const type = item.asset_type || 'stock';
      const value = item.current_value || 0;
      allocation[type] = (allocation[type] || 0) + value;
    });
    
    return Object.entries(allocation).map(([type, value]) => ({
      name: ASSET_LABELS[type]?.[language === 'sw' ? 'sw' : 'en'] || type,
      value,
      type,
      color: ASSET_COLORS[type]?.color || '#666'
    }));
  };

  const chartData = getChartData();
  const allocationData = getAllocationData();
  const finalValues = chartData[chartData.length - 1] || {};

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!portfolio?.items?.length) {
    return (
      <Layout>
        <div className="page-enter max-w-2xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('Back', 'Rudi')}
          </Button>
          
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <Target className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t('No Investments to Simulate', 'Hakuna Uwekezaji wa Kusimulisha')}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t('Add investments to your portfolio to see future projections across all asset classes', 'Ongeza uwekezaji kwenye portfolio yako kuona makadirio ya baadaye kwa mali zote')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/portfolio/add')} className="rounded-full" data-testid="add-stock-btn">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {t('Add Stock', 'Ongeza Hisa')}
                </Button>
                <Button onClick={() => navigate('/portfolio/add?type=utt')} variant="outline" className="rounded-full border-teal-300 text-teal-700">
                  <PieChart className="w-4 h-4 mr-2" />
                  {t('Add UTT Fund', 'Ongeza UTT')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-enter space-y-6">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('Back', 'Rudi')}
          </Button>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
              {t('Portfolio Future Simulation', 'Usimulizi wa Portfolio ya Baadaye')}
            </h1>
            <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
              <Sparkles className="w-3 h-3 mr-1" />
              {t('AI Powered', 'AI')}
            </Badge>
          </div>
          <p className="text-muted-foreground">
            {t('Simulate how your investments might grow based on all 28 DSE stocks, UTT funds, ETFs, and bonds', 'Simula jinsi uwekezaji wako unavyoweza kukua kulingana na hisa 28 za DSE, mifuko ya UTT, ETFs, na bondi')}
          </p>
        </div>

        {/* Current Portfolio Summary */}
        <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t('Current Value', 'Thamani ya Sasa')}
                </p>
                <p className="text-xl font-bold text-foreground" data-testid="current-value">
                  {formatCurrency(simulation?.current_value || portfolio.summary?.total_current_value)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t('Holdings', 'Milki')}
                </p>
                <p className="text-xl font-bold text-foreground">
                  {portfolio.items.length} {t('assets', 'mali')}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t('Asset Types', 'Aina za Mali')}
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {allocationData.map(asset => (
                    <Badge key={asset.type} variant="outline" className="text-xs" style={{ borderColor: asset.color, color: asset.color }}>
                      {asset.name}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t('Projection Period', 'Kipindi')}
                </p>
                <p className="text-xl font-bold text-primary">
                  {years} {t('years', 'miaka')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview" data-testid="tab-overview">
              {t('Overview', 'Muhtasari')}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              {t('Settings', 'Mipangilio')}
            </TabsTrigger>
            <TabsTrigger value="breakdown" data-testid="tab-breakdown">
              {t('Breakdown', 'Uchambuzi')}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            {/* Projection Chart */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  {t('Projected Portfolio Growth', 'Ukuaji Unaokadirikia')}
                  {simulating && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorAggressive" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorModerate" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => formatNumber(value)}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [formatCurrency(value), name]}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="aggressive" 
                        name={t('Aggressive', 'Mkali')}
                        stroke="#22c55e" 
                        strokeWidth={2}
                        fill="url(#colorAggressive)"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="moderate" 
                        name={t('Moderate', 'Wastani')}
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        fill="url(#colorModerate)"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="conservative" 
                        name={t('Conservative', 'Mhafidhina')}
                        stroke="hsl(var(--accent))" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="contributions" 
                        name={t('Contributions', 'Michango')}
                        stroke="#888888" 
                        strokeWidth={1}
                        strokeDasharray="3 3"
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Scenario Results */}
            <div className="grid sm:grid-cols-3 gap-4">
              {/* Aggressive */}
              <Card className="border-green-200 bg-green-50/50 dark:bg-green-900/10 cursor-pointer hover:border-green-400 transition-colors" onClick={() => setScenario('aggressive')}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-green-700 dark:text-green-400">{t('Aggressive', 'Mkali')}</p>
                    {scenario === 'aggressive' && <Badge className="bg-green-600 text-white text-xs">{t('Selected', 'Imechaguliwa')}</Badge>}
                  </div>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-400" data-testid="aggressive-value">
                    {formatCurrency(simulation?.scenarios?.aggressive?.projected_value || finalValues.aggressive)}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                    +{simulation?.scenarios?.aggressive?.annual_return || 15}% {t('annual return', 'faida kwa mwaka')}
                  </p>
                </CardContent>
              </Card>

              {/* Moderate */}
              <Card className={`border-primary/30 bg-primary/5 cursor-pointer hover:border-primary transition-colors ${scenario === 'moderate' ? 'ring-2 ring-primary' : ''}`} onClick={() => setScenario('moderate')}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-primary">{t('Moderate', 'Wastani')}</p>
                    {scenario === 'moderate' && <Badge className="bg-primary text-white text-xs">{t('Selected', 'Imechaguliwa')}</Badge>}
                  </div>
                  <p className="text-2xl font-bold text-primary" data-testid="moderate-value">
                    {formatCurrency(simulation?.scenarios?.moderate?.projected_value || simulation?.projected_value)}
                  </p>
                  <p className="text-xs text-primary/80 mt-1">
                    +{simulation?.scenarios?.moderate?.annual_return || simulation?.annualized_return || 10}% {t('annual return', 'faida kwa mwaka')}
                  </p>
                </CardContent>
              </Card>

              {/* Conservative */}
              <Card className="border-accent/30 bg-accent/5 cursor-pointer hover:border-accent transition-colors" onClick={() => setScenario('conservative')}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-accent">{t('Conservative', 'Mhafidhina')}</p>
                    {scenario === 'conservative' && <Badge className="bg-accent text-white text-xs">{t('Selected', 'Imechaguliwa')}</Badge>}
                  </div>
                  <p className="text-2xl font-bold text-accent" data-testid="conservative-value">
                    {formatCurrency(simulation?.scenarios?.conservative?.projected_value || finalValues.conservative)}
                  </p>
                  <p className="text-xs text-accent/80 mt-1">
                    +{simulation?.scenarios?.conservative?.annual_return || 7}% {t('annual return', 'faida kwa mwaka')}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Key Metrics */}
            {simulation && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground mb-1">{t('Total Contributions', 'Michango Jumla')}</p>
                    <p className="text-lg font-bold text-foreground">{formatCurrency(simulation.total_contributions)}</p>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground mb-1">{t('Projected Returns', 'Faida Inayokadiriwa')}</p>
                    <p className="text-lg font-bold text-green-600">+{formatCurrency(simulation.projected_returns)}</p>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground mb-1">{t('Annualized Return', 'Faida ya Mwaka')}</p>
                    <p className="text-lg font-bold text-primary">{simulation.annualized_return?.toFixed(1)}%</p>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground mb-1">{t('Growth Multiple', 'Mara za Ukuaji')}</p>
                    <p className="text-lg font-bold text-foreground">
                      {simulation.current_value > 0 ? ((simulation.projected_value / simulation.current_value).toFixed(2)) : 0}x
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6 mt-6">
            {/* Year Slider */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  {t('Projection Timeline', 'Muda wa Makadirio')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground">{t('Years', 'Miaka')}</span>
                  <span className="text-2xl font-bold text-primary">{years}</span>
                </div>
                <Slider
                  value={[years]}
                  onValueChange={(value) => setYears(value[0])}
                  min={1}
                  max={15}
                  step={1}
                  className="py-4"
                  data-testid="years-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>1 {t('year', 'mwaka')}</span>
                  <span>15 {t('years', 'miaka')}</span>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Contribution Slider */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Plus className="w-4 h-4 text-primary" />
                  {t('Monthly Contribution', 'Mchango wa Kila Mwezi')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground">{t('Amount', 'Kiasi')}</span>
                  <span className="text-2xl font-bold text-primary">{formatCurrency(monthlyContribution)}</span>
                </div>
                <Slider
                  value={[monthlyContribution]}
                  onValueChange={(value) => setMonthlyContribution(value[0])}
                  min={0}
                  max={1000000}
                  step={10000}
                  className="py-4"
                  data-testid="contribution-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>TZS 0</span>
                  <span>TZS 1,000,000</span>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {t('Total annual contribution:', 'Mchango wa jumla wa mwaka:')} <span className="font-semibold text-foreground">{formatCurrency(monthlyContribution * 12)}</span>
                </p>
              </CardContent>
            </Card>

            {/* Scenario Selection */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  {t('Growth Scenario', 'Hali ya Ukuaji')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-3 gap-3">
                  {['conservative', 'moderate', 'aggressive'].map((s) => (
                    <button
                      key={s}
                      onClick={() => setScenario(s)}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        scenario === s 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/30'
                      }`}
                      data-testid={`scenario-${s}`}
                    >
                      <p className="font-semibold text-foreground capitalize">
                        {t(s.charAt(0).toUpperCase() + s.slice(1), 
                           s === 'conservative' ? 'Mhafidhina' : s === 'moderate' ? 'Wastani' : 'Mkali')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {s === 'conservative' ? '6-8%' : s === 'moderate' ? '10-12%' : '14-18%'}
                      </p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Breakdown Tab */}
          <TabsContent value="breakdown" className="space-y-6 mt-6">
            {/* Asset Allocation Pie Chart */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-primary" />
                  {t('Asset Allocation', 'Mgawanyo wa Mali')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={allocationData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={allocationData.length > 1 ? 5 : 0}
                          dataKey="value"
                        >
                          {allocationData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ 
                            background: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    {allocationData.map((asset) => {
                      const Icon = ASSET_ICONS[asset.type] || Wallet;
                      const totalValue = allocationData.reduce((sum, a) => sum + a.value, 0);
                      const percentage = totalValue > 0 ? ((asset.value / totalValue) * 100).toFixed(1) : 0;
                      return (
                        <div 
                          key={asset.type}
                          className={`flex items-center justify-between p-3 rounded-lg ${ASSET_COLORS[asset.type]?.bg || 'bg-muted/50'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: asset.color + '20' }}>
                              <Icon className="w-5 h-5" style={{ color: asset.color }} />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{asset.name}</p>
                              <p className="text-xs text-muted-foreground">{formatCurrency(asset.value)}</p>
                            </div>
                          </div>
                          <Badge variant="outline" style={{ borderColor: asset.color, color: asset.color }}>
                            {percentage}%
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Holdings Detail */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  {t('Portfolio Holdings', 'Milki za Portfolio')} ({portfolio.items.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {portfolio.items.map((item, index) => {
                    const Icon = ASSET_ICONS[item.asset_type] || TrendingUp;
                    const colors = ASSET_COLORS[item.asset_type] || ASSET_COLORS.stock;
                    return (
                      <div 
                        key={item.id || index}
                        className={`flex items-center justify-between p-3 rounded-lg ${colors.bg} hover:opacity-90 cursor-pointer transition-opacity`}
                        onClick={() => item.asset_type === 'stock' ? navigate(`/stocks/${item.symbol}`) : navigate(item.symbol === 'FAIDA' ? '/faida' : '/utt-amis')}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
                            <Icon className={`w-5 h-5 ${colors.text}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-foreground">{item.symbol || item.stock_name}</p>
                              <Badge variant="outline" className={`text-xs ${colors.border} ${colors.text}`}>
                                {ASSET_LABELS[item.asset_type]?.[language === 'sw' ? 'sw' : 'en'] || item.asset_type}
                              </Badge>
                              {item.source === 'wekeza_utt' && (
                                <Badge variant="outline" className={`text-xs ${item.allocation_status === 'allocated' ? 'border-emerald-300 text-emerald-700' : 'border-amber-300 text-amber-700'}`} data-testid={`holding-utt-status-${item.id}`}>
                                  {item.allocation_status === 'allocated' ? t('Via Wekeza · allocated', 'Kupitia Wekeza · imegawiwa') : t('Via Wekeza · pending allocation', 'Kupitia Wekeza · inasubiri kugawiwa')}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {item.quantity?.toLocaleString()} {item.asset_type === 'utt' ? t('units', 'vipande') : t('shares', 'hisa')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-foreground">{formatCurrency(item.current_value)}</p>
                          <p className={`text-xs ${item.gain_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {item.gain_loss >= 0 ? '+' : ''}{item.gain_loss_percent?.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Yearly Breakdown Table */}
            {simulation?.yearly_breakdown && (
              <Card className="border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    {t('Year-by-Year Projection', 'Makadirio ya Kila Mwaka')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border text-left">
                          <th className="pb-3 text-sm font-medium text-muted-foreground">{t('Year', 'Mwaka')}</th>
                          <th className="pb-3 text-sm font-medium text-muted-foreground text-right">{t('Start Value', 'Thamani ya Mwanzo')}</th>
                          <th className="pb-3 text-sm font-medium text-muted-foreground text-right">{t('Contributions', 'Michango')}</th>
                          <th className="pb-3 text-sm font-medium text-muted-foreground text-right">{t('Returns', 'Faida')}</th>
                          <th className="pb-3 text-sm font-medium text-muted-foreground text-right">{t('End Value', 'Thamani ya Mwisho')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {simulation.yearly_breakdown.map((year) => (
                          <tr key={year.year} className="border-b border-border/50 hover:bg-muted/30">
                            <td className="py-3 font-medium text-foreground">{t('Year', 'Mwaka')} {year.year}</td>
                            <td className="py-3 text-right text-muted-foreground">{formatCurrency(year.start_value)}</td>
                            <td className="py-3 text-right text-muted-foreground">{formatCurrency(year.contributions)}</td>
                            <td className="py-3 text-right text-green-600">+{formatCurrency(year.returns)}</td>
                            <td className="py-3 text-right font-semibold text-foreground">{formatCurrency(year.end_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={() => navigate('/simulator')}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            {t('Try Simulator', 'Jaribu Simulator')}
          </Button>
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={() => navigate('/portfolio/add')}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('Add More Assets', 'Ongeza Mali Zaidi')}
          </Button>
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={() => navigate('/intelligence')}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {t('Portfolio Intelligence', 'Akili ya Portfolio')}
          </Button>
        </div>

        {/* Disclaimer */}
        <Card className="bg-muted/50 border-0">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                {t(
                  'These projections are based on historical performance and estimated returns for different asset classes (DSE stocks, UTT funds, ETFs, bonds). They are for educational purposes only and do not guarantee future results. Market conditions, economic factors, and company performance can significantly impact actual returns. Always consult with a licensed financial advisor before making investment decisions.',
                  'Makadirio haya yanategemea utendaji wa kihistoria na faida inayokadiriwa kwa aina tofauti za mali (hisa za DSE, mifuko ya UTT, ETFs, bondi). Ni kwa madhumuni ya elimu tu na hayahakikishi matokeo ya baadaye. Hali za soko, mambo ya kiuchumi, na utendaji wa kampuni vinaweza kuathiri sana faida halisi. Daima wasiliana na mshauri wa kifedha aliye na leseni kabla ya kufanya maamuzi ya uwekezaji.'
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
